/**
 * overlayQueue — centrale beleefdheids-scheduler voor pop-ups/overlays.
 *
 * Doel: voorkomen dat meerdere modals/banners tegelijk of vlak achter elkaar
 * verschijnen. Elk auto-verschijnend UI-element registreert zich hier en
 * wacht op groen licht.
 *
 * Regels:
 *  - Er is maximaal ÉÉN actieve overlay tegelijk.
 *  - Hoogste prioriteit wint. Als er meerdere wachten, gaat de hoogste.
 *  - Na een release geldt een cooldown zodat de volgende overlay niet
 *    direct in beeld springt (rustiger voor de gebruiker).
 *
 * Prioriteiten (hoog → laag):
 *  100  cookie          (wettelijk verplicht, altijd eerst)
 *   90  login-modal     (bypass — gebruiker heeft zelf klik gedaan)
 *   70  app-tour        (informatieve rondleiding, eenmalig per bezoeker)
 *   60  organizer-welcome (eerste inlog voor organisator)
 *   40  install-prompt  (PWA install banner)
 *   20  feedback        (pop-up vragenlijst)
 */

const COOLDOWN_MS = 60 * 1000; // 60 sec adempauze tussen twee overlays

let active = null;              // { name, priority }
let lastReleaseAt = 0;
const waiting = new Map();      // name -> { priority, callback }
let dispatchTimer = null;

function tryDispatch() {
  if (active) return;
  if (waiting.size === 0) return;

  const now = Date.now();
  if (lastReleaseAt > 0 && now - lastReleaseAt < COOLDOWN_MS) {
    if (dispatchTimer) clearTimeout(dispatchTimer);
    dispatchTimer = setTimeout(tryDispatch, COOLDOWN_MS - (now - lastReleaseAt) + 50);
    return;
  }

  // Kies de hoogste-prioriteit wachtende overlay
  let best = null;
  for (const [name, entry] of waiting.entries()) {
    if (!best || entry.priority > best.priority) {
      best = { name, ...entry };
    }
  }
  if (!best) return;

  waiting.delete(best.name);
  active = { name: best.name, priority: best.priority };
  try {
    best.callback(true);
  } catch (e) {
    // Als een callback faalt, geef anderen de kans
    active = null;
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('hoibuur:overlay-changed', {
      detail: { active: best.name },
    }));
  }
}

export function requestOverlay(name, priority, callback) {
  waiting.set(name, { priority, callback });
  tryDispatch();
}

export function releaseOverlay(name) {
  const wasActive = active && active.name === name;
  waiting.delete(name);
  if (wasActive) {
    active = null;
    lastReleaseAt = Date.now();
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('hoibuur:overlay-changed', {
        detail: { active: null },
      }));
    }
    tryDispatch();
  }
}

export function currentActive() {
  return active ? active.name : null;
}

// Voor debug in devtools
if (typeof window !== 'undefined') {
  window.__hbOverlayQueue = { requestOverlay, releaseOverlay, currentActive };
}
